/**
 * @author Maria Osama
 * @copyright Copyright 2020 by Radivision Inc., CA, USA. All Rights Reserved.
 * @Date: 2018-07-0 15:35:45
 * @description Implementation of the Report a Bug react component
 * @filename report-bug.tsx
 */
import React from "react";
import { gaEvent } from "../../../utilities/general";
import { Router } from "../../../utilities/router";
import { Loader } from "../../page/loader";
import { RdvButton } from "../rdv-button";
import { SupportIssueKind } from "@radivision/graphql/lib/ts/graphql/support-issue-kind";
import { CreateSupportIssueInput } from "@radivision/graphql/lib/ts/graphql/mutations/create-support-issue-input";
import { MutationController } from "../../../mutation-controller";

/**
 * The props of the Report a Bug component.
 *
 * @interface
 */
interface ReportBugProps {
  environment: any;
}

/**
 * The state of the report a bug component.
 *
 * @export
 * @interface
 */
interface ReportBugState {
  /**
   * show or hide warning mesage.
   *
   * @type {string}
   */
  class: string;

  /**
   * A flag which is true if the message length is valid.
   *
   * @type {boolean}
   */
  messageLengthIsValid: boolean;

  /**
   * The value of the Issue description.
   *
   * @type {string}
   */
  value: string;

  /**
   * name of the user reporting the issue.
   *
   * @type {string}
   * @memberof ReportBugState
   */
  name?: string;

  /**
   * mail of the user reporting the issue.
   *
   * @type {string}
   * @memberof ReportBugState
   */
  mail: string;

  /**
   * type of the issue reported.
   *
   * @type {string}
   * @memberof ReportBugState
   */
  issueType: SupportIssueKind;

  /**
   * type of the device the issue appeared in.
   *
   * @type {string}
   * @memberof ReportBugState
   */
  issueDevice?: string;

  /**
   * a boolean which is true if the request is active.
   *
   * @type {boolean}
   * @memberof ReportBugState
   */
  submittingIssue: boolean;
  /**
   *
   *
   * @type {boolean}
   * @memberof ReportBugState
   */
  submittingSuccess?: boolean;
  /**
   * a boolean which is true on sending the message
   * to enable the Progress bar
   */
  loading: boolean;
  /**
   *
   *
   * @type {boolean}
   * @memberof ReportBugState
   */
  reportBugErrors: { hasError: boolean; errors: string[] };
  /**
   *
   *
   * @type {string}
   * @memberof ReportBugState
   */
  successMessage: string;
}

/**
 *
 * A React component that renders the report a bug footer.
 *
 * @extends {React.Component}
 */
export class ReportBug extends React.Component<ReportBugProps, ReportBugState> {
  /**
   * Constructor.
   *
   * @param {ContactUsProps} props The props of the component.
   */
  constructor(props: ReportBugProps) {
    super(props);
    this.onMessageChange = this.onMessageChange.bind(this);
    this.onNameChange = this.onNameChange.bind(this);
    this.onEmailChange = this.onEmailChange.bind(this);
    this.onIssueDeviceChange = this.onIssueDeviceChange.bind(this);
    this.onTypeChange = this.onTypeChange.bind(this);
    this.reportIssue = this.reportIssue.bind(this);
    this.state = {
      messageLengthIsValid: false,
      value: "",
      class: "hide",
      mail: null,
      issueType: SupportIssueKind.OTHER,
      submittingIssue: false,
      submittingSuccess: false,
      loading: false,
      reportBugErrors: { hasError: false, errors: [] },
      successMessage: "",
    };
  }

  /**
   * Invoked when a change is made to the description of the Issue.
   *
   * @param {React.ChangeEvent<React.ChangeEvent<HTMLTextAreaElement>>} event The change event.
   */
  onMessageChange(event: React.ChangeEvent<HTMLTextAreaElement>) {
    const charactersCount: number = event.target.value.length;
    const target: any = event.target;

    this.setState({
      value: event.target.value,
    });
    if (charactersCount >= 250) {
      this.setState({
        messageLengthIsValid: true,
      });
      target
        .closest(".form-group")
        .querySelector(".invalid-feedback").style.display = "block";
    } else {
      this.setState({
        messageLengthIsValid: false,
      });
      target
        .closest(".form-group")
        .querySelector(".invalid-feedback").style.display = "none";
    }
  }

  /**
   * Invoke when Type is Changed.
   *
   * @param {React.SyntheticEvent} event
   * @memberof ReportBug
   */
  onTypeChange(e: any) {
    this.setState({ issueType: e.target.value });
  }

  /**
   * Invoked when the name value is changed.
   *
   * @param {React.FormEvent<HTMLInputElement>} e The event.
   */
  onNameChange(e: React.FormEvent<HTMLInputElement>) {
    this.setState({ name: e.currentTarget.value });
  }

  /**
   * Invoked when the email value is changed.
   *
   * @param {React.FormEvent<HTMLInputElement>} e The event.
   */
  onEmailChange(e: React.FormEvent<HTMLInputElement>) {
    this.setState({ mail: e.currentTarget.value });
  }

  /**
   * Invoked when the device value is changed.
   *
   * @param {React.FormEvent<HTMLInputElement>} e The event.
   */
  onIssueDeviceChange(e: React.FormEvent<HTMLInputElement>) {
    this.setState({ issueDevice: e.currentTarget.value });
  }

  /**
   * function to be called when the user needs to report an issue.
   *
   * @memberof ReportBug
   */
  reportIssue(e: React.FormEvent): Promise<any> {
    let reportIssueInput: CreateSupportIssueInput;
    this.setState({
      loading: true,
      reportBugErrors: { hasError: false, errors: [] },
      successMessage: "",
    });

    e.preventDefault();

    if (
      this.state.value.length > 0 &&
      this.state.mail !== null &&
      this.state.mail !== undefined
    ) {
      reportIssueInput = {
        kind: this.state.issueType,
        clientMutationId: undefined,
        description: this.state.value,
        email: this.state.mail,
      };
      if (this.state.name !== undefined && this.state.name !== null) {
        reportIssueInput.name = this.state.name;
      }
      if (
        this.state.issueDevice !== undefined &&
        this.state.issueDevice !== null
      ) {
        reportIssueInput.browserAndDeviceType = this.state.issueDevice;
      }

      this.setState({
        submittingIssue: true,
      });

      let promise = MutationController.createSupportIssue(
        reportIssueInput
      );
      promise = promise.then((res) => {
        this.setState({
          loading: false,
          submittingIssue: false,
          submittingSuccess: true,
          successMessage:
            "Thank You! Your report will be reviewed and you will receive a reply soon.",
        });

        gaEvent(
          "Support / Report Bug",
          "Click",
          "Message Send To Support Successfully!",
          true
        );
        let form: any;
        form = document.getElementById("report-issue-form");
        form.reset();

        return Promise.resolve(res);
      });

      promise = promise.catch((err) => {
        let reportBugErrors = this.state.reportBugErrors;
        reportBugErrors = {
          hasError: true,
          errors: ["Unexpected error contact Radivision Technical team."],
        };
        this.setState({
          reportBugErrors,
          loading: false,
          submittingSuccess: true,
        });

        return Promise.reject(err);
      });

      return promise;

      // return new Promise(
      //   (resolve: (response: any) => void, reject: (e: any) => void): void => {
      //     _commitMutation(this.props.environment, {
      //       mutation: CREATE_SUPPORT_ISSUE_GRAPHQL_MUTATION,
      //       variables: {
      //         input: reportIssueInput
      //       },
      //       onCompleted: (response: any, errors: Error[]) => {
      //         if (errors !== undefined && errors !== null) {
      //           reject(errors);
      //           let reportBugErrors = this.state.reportBugErrors;
      //           reportBugErrors = {
      //             hasError: true,
      //             errors: [
      //               "Unexpected error contact Radivision Technical team."
      //             ]
      //           };
      //           this.setState({
      //             reportBugErrors,
      //             loading: false,
      //             submittingSuccess: true
      //           });
      //         } else {
      //           resolve(response);
      //           this.setState({
      //             loading: false,
      //             submittingIssue: false,
      //             submittingSuccess: true,
      //             successMessage:
      //               "Thank You! Your report will be reviewed and you will receive a replay soon."
      //           });
      //         }
      //         gaEvent(
      //           "Support / Report Bug",
      //           "Click",
      //           "Message Send To Support Successfully!",
      //           true
      //         );
      //         let form: any;
      //         form = document.getElementById("report-issue-form");
      //         form.reset();
      //       },
      //       onError: (err: Error) => {
      //         let reportBugErrors = this.state.reportBugErrors;
      //         reportBugErrors = {
      //           hasError: true,
      //           errors: ["Unexpected error contact Radivision Technical team."]
      //         };
      //         if (err) {
      //           reportBugErrors.errors = [err.message];
      //         }
      //         this.setState({
      //           reportBugErrors,
      //           submittingIssue: false,
      //           submittingSuccess: true,
      //           loading: false
      //         });
      //       }
      //     });
      //   }
      // );
    }
  }

  /**
   * Returns a ReactNode containing the rendered component.
   *
   * @returns {React.ReactNode} The ReactNode containing the rendered component.
   */
  render(): React.ReactNode {
    return (
      <div id="report-bug" className="external inner-pages">
        <h1 className="font-weight-bold">Report an Issue</h1>
        <h4 className="font-weight-400">
          We're always looking for ways to make our community better for you.
        </h4>
        <div className="row">
          <div className="col-md-6">
            <form
              id="report-issue-form"
              onSubmit={this.reportIssue}
              className="add-media-modal"
            >
              {this.state.submittingSuccess ? (
                <div>
                  {this.state.reportBugErrors.hasError ? (
                    <div className="alert alert-danger" role="alert">
                      {this.state.reportBugErrors.errors[0]}
                    </div>
                  ) : (
                    <div className="alert alert-success" role="alert">
                      {this.state.successMessage}
                    </div>
                  )}
                </div>
              ) : null}
              <div className="form-group">
                <label className="has-float-label">Full Name</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Your full name"
                  onChange={this.onNameChange}
                  required={true}
                  disabled={this.state.loading}
                />
              </div>
              <div className="form-group">
                <label className="has-float-label">Email</label>
                <input
                  type="email"
                  className="form-control"
                  placeholder="Your email"
                  onChange={this.onEmailChange}
                  required={true}
                  disabled={this.state.loading}
                />
              </div>
              <h3 className="title font-weight-bold">Type of Issue</h3>
              <h3 className="font-weight-400 mt-4">Please select one</h3>
              <div className="issue-types text-uppercase">
                <div>
                  <input
                    type="radio"
                    name="optradio"
                    value={SupportIssueKind.WEBSITE_CONTENT}
                    onChange={this.onTypeChange}
                    checked={
                      this.state.issueType === SupportIssueKind.WEBSITE_CONTENT
                    }
                    required={true}
                    disabled={this.state.loading}
                  />
                  Website content
                </div>
                <div>
                  <input
                    type="radio"
                    name="optradio"
                    value={SupportIssueKind.WEBSITE_BEHAVIOR}
                    onChange={this.onTypeChange}
                    checked={
                      this.state.issueType === SupportIssueKind.WEBSITE_BEHAVIOR
                    }
                    required={true}
                    disabled={this.state.loading}
                  />
                  Website behavior
                </div>
                <div>
                  <input
                    type="radio"
                    name="optradio"
                    value={SupportIssueKind.API}
                    onChange={this.onTypeChange}
                    checked={this.state.issueType === SupportIssueKind.API}
                    required={true}
                    disabled={this.state.loading}
                  />
                  API
                </div>
                <div>
                  <input
                    type="radio"
                    name="optradio"
                    value={SupportIssueKind.OTHER}
                    onChange={this.onTypeChange}
                    checked={this.state.issueType === SupportIssueKind.OTHER}
                    required={true}
                    disabled={this.state.loading}
                  />
                  Other
                </div>
              </div>
              <div className="form-group">
                <label className="has-float-label mt-4">
                  Browser and Device Type
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Your browser, browser version and device type"
                  onChange={this.onIssueDeviceChange}
                  disabled={this.state.loading}
                />
              </div>
              <div className="form-group textarea padding-top">
                <label className="has-float-label mt-4">Message</label>
                <textarea
                  className={`form-control ${
                    this.state.messageLengthIsValid ? "is-invalid" : ""
                  }`}
                  placeholder="A description of your issue with as much detail as you can"
                  onChange={this.onMessageChange}
                  required={true}
                  maxLength={250}
                  disabled={this.state.loading}
                />
                <div className="invalid-feedback" style={{ display: "none" }}>
                  The message text should have maximum of 250 characters only!
                </div>
              </div>
              <RdvButton
                type="submit"
                form="report-issue-form"
                text="Report Issue"
                extraClassName="contactButton"
                disabled={this.state.submittingIssue}
                isLoading={this.state.loading}
              />
            </form>
          </div>
          <div className="col-md-6 right-side">
            <div className="right-side-content">
              <div>
                <h3>Things people like to ask us:</h3>
                <h3>
                  <a onClick={() => Router.to("support", undefined, "faqs")}>
                    See our FAQs
                  </a>
                </h3>
              </div>
              <div>
                <h3>Common browser and device issue that you may encounter:</h3>
                <h3>
                  <a
                    onClick={() =>
                      Router.to("support", undefined, "technical-issues")
                    }
                  >
                    Technical Issues
                  </a>
                </h3>
              </div>
              <div>
                <h3>Connect with us at : </h3>
                <h3>
                  <a
                    className="support-email"
                    href="mailto:support@radivision.com"
                  >
                    support@radivision.com
                  </a>
                </h3>

                <h3 className="or"> or </h3>
                <h3>
                  <a
                    onClick={() =>
                      Router.to("support", undefined, "contact-us")
                    }
                  >
                    Contact Us
                  </a>
                </h3>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
